import * as React from 'react';
import { AnyAction, Dispatch } from 'redux';
import { takeEvery, takeLeading } from 'redux-saga/effects';
import { CreateEventRequest as ICreateEventRequest } from 'stageclip-common';

import { AssignEventRequest } from '../api/definitions/assignEventRequest';
import { CreateEventRequest } from '../api/definitions/createEventRequest';
import { SendToNlpRequest } from '../api/definitions/sendToNlpRequest';
import { CreateVgGeneratorJobRequest } from '../api/definitions/createVgGeneratorJobRequest';
import { DeleteParticipantsDataRequest } from '../api/definitions/deleteParticipantsDataRequest';
import { DeleteParticipantsZipRequest } from '../api/definitions/deleteParticipantsZipRequest';
import { DeleteParticipantsVgZipRequest } from '../api/definitions/deleteParticipantsVgZipRequest';
import { DeleteParticipantsVgVideoRequest } from '../api/definitions/deleteParticipantsVgVideoRequest';
import { EditEventRequest } from '../api/definitions/editEventRequest';
import { GenerateParticipantsZipRequest } from '../api/definitions/generateParticipantsZipRequest';
import { GenerateParticipantsVgZipRequest } from '../api/definitions/generateParticipantsVgZipRequest';
import { GetAllEventsRequest } from '../api/definitions/getAllEventsRequest';
import { GetEventRequest } from '../api/definitions/getEventRequest';
import * as EntityNormalizers from '../api/normalizers/entityNormalizers';
import * as EventNormalizers from '../api/normalizers/eventNormalizers';
import * as EventSendToNlpNormalizers from '../api/normalizers/eventSendToNlpNormalizer';
import { DataRequestActionTypes, InternalActionTypes } from '../constants/actionTypes';
import { formatClipConfiguration } from '../helpers/clipConfigurationHelper';

import { RequestAction } from './allActions';
import { createAllEventActionsRequestedAction } from './eventActionsActions';
import { FormSubmitAction } from './formActions';
import { createAllEventParticipantsRequestedAction } from './participantActions';
import { runApiRequest, throttleRequests } from './saga/sagaActions';

export interface ActiveEventsLoadedAction extends AnyAction {
  type: InternalActionTypes.ActiveEventsLoaded;
  result: EntityNormalizers.NormalizedEntityArray<EventNormalizers.NormalizedEvents>;
}

export interface ArchivedEventsLoadedAction extends AnyAction {
  type: InternalActionTypes.ArchivedEventsLoaded;
  result: EntityNormalizers.NormalizedEntityArray<EventNormalizers.NormalizedEvents>;
}

export interface EventCancelledAction extends AnyAction {
  type: InternalActionTypes.EventCancelled;
  result: EntityNormalizers.NormalizedEntityArray<EventNormalizers.NormalizedEvents>;
}

export interface UnlockTemplateAction extends AnyAction {
  type: InternalActionTypes.UnlockTemplate;
  result: EntityNormalizers.NormalizedEntityArray<EventNormalizers.NormalizedEvents>;
}

export interface EventAssignedAction extends AnyAction {
  type: InternalActionTypes.EventAssigned;
  result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>;
}

export interface CreateEventRequestedAction extends FormSubmitAction<ICreateEventRequest> {
  type: DataRequestActionTypes.CreateEventRequested;
}

export interface SendEventToNlpRequestedAction extends RequestAction {
  eventId: string;
  result: EntityNormalizers.NormalizedSingleEntity<EventSendToNlpNormalizers.NormalizedEventsSendToNlpView>;
  type: DataRequestActionTypes.SendToNLP;
}

export interface EditEventRequestedAction extends FormSubmitAction<ICreateEventRequest> {
  type: DataRequestActionTypes.EditEventRequested;
  eventId: string;
}

export interface EventLoadedAction extends AnyAction {
  type: InternalActionTypes.EventLoaded;
  eventId: string;
  result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>;
}

export interface EventEditedAction extends AnyAction {
  type: InternalActionTypes.EventEdited;
  result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>;
}

export interface EventCreatedAction extends AnyAction {
  type: InternalActionTypes.EventCreated;
  result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>;
}

export interface EventArchivedAction extends AnyAction {
  type: InternalActionTypes.EventArchived;
  result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>;
}

export interface EventUnarchivedAction extends AnyAction {
  type: InternalActionTypes.EventUnarchived;
  result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>;
}

export interface RequestActiveEventsAction extends RequestAction {
  type: DataRequestActionTypes.ActiveEventsRequested;
}

export interface RequestArchivedEventsAction extends RequestAction {
  type: DataRequestActionTypes.ArchivedEventsRequested;
}

export interface RequestAssignEventAction extends RequestAction {
  eventId: string;
  userId: string;
  type: DataRequestActionTypes.AssignEventRequested;
}

export interface RequestEventAction extends RequestAction {
  type: DataRequestActionTypes.EventRequested;
  eventId: string;
}

export interface GenerateParticipantsZipAction extends RequestAction {
  type: DataRequestActionTypes.GenerateParticipantsZipRequested;
  eventId: string;
}

export interface DeleteParticipantsZipAction extends RequestAction {
  type: DataRequestActionTypes.DeleteParticipantsZipRequested;
  eventId: string;
}

export interface GenerateParticipantsVgZipAction extends RequestAction {
  type: DataRequestActionTypes.GenerateParticipantsVgZipRequested;
  eventId: string;
}

export interface DeleteParticipantsVgZipAction extends RequestAction {
  type: DataRequestActionTypes.DeleteParticipantsVgZipRequested;
  eventId: string;
}

export interface DeleteParticipantsVgVideoAction extends RequestAction {
  type: DataRequestActionTypes.DeleteParticipantsVgVideoRequested;
  eventId: string;
}

export interface GenerateVgVideoCeremonyAction extends FormSubmitAction<any> {
  type: DataRequestActionTypes.GenerateVgVideoCeremonyRequested;
  eventId: string;
}

export interface DeleteParticipantsDataAction extends RequestAction {
  type: DataRequestActionTypes.DeleteParticipantsDataRequested;
  eventId: string;
}

function createActiveEventsLoadedAction(
  result: EntityNormalizers.NormalizedEntityArray<EventNormalizers.NormalizedEvents>,
): ActiveEventsLoadedAction {
  return {
    result,
    type: InternalActionTypes.ActiveEventsLoaded,
  };
}

function createArchivedEventsLoadedAction(
  result: EntityNormalizers.NormalizedEntityArray<EventNormalizers.NormalizedEvents>,
): ArchivedEventsLoadedAction {
  return {
    result,
    type: InternalActionTypes.ArchivedEventsLoaded,
  };
}

function createEventAssignedAction(
  result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>,
): EventAssignedAction {
  return {
    result,
    type: InternalActionTypes.EventAssigned,
  };
}

function createEventLoadedAction(
  eventId: string,
  result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>,
): EventLoadedAction {
  return {
    eventId,
    result,
    type: InternalActionTypes.EventLoaded,
  };
}

function createEventEditedAction(
  result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>,
): EventEditedAction {
  return {
    result,
    type: InternalActionTypes.EventEdited,
  };
}

function createEventCreatedAction(
  result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>,
): EventCreatedAction {
  return {
    result,
    type: InternalActionTypes.EventCreated,
  };
}

function createEventSendToNlpAction(
  result: EntityNormalizers.NormalizedSingleEntity<EventSendToNlpNormalizers.NormalizedEventsSendToNlpView>,
  component: React.Component,
  dispatch: Dispatch,
  eventId: string,
): SendEventToNlpRequestedAction {
  return {
    result,
    dispatch,
    requestingComponent: component,
    type: DataRequestActionTypes.SendToNLP,
    eventId,
  };
}

export function createActiveEventsRequestedAction(
  component: React.Component,
  dispatch: Dispatch,
): RequestActiveEventsAction {
  return {
    dispatch,
    requestingComponent: component,
    type: DataRequestActionTypes.ActiveEventsRequested,
  };
}

export function createArchivedEventsRequestedAction(
  component: React.Component,
  dispatch: Dispatch,
): RequestArchivedEventsAction {
  return {
    dispatch,
    requestingComponent: component,
    type: DataRequestActionTypes.ArchivedEventsRequested,
  };
}

export function createAssignEventRequestAction(
  eventId: string,
  userId: string,
  component: React.Component,
  dispatch: Dispatch,
): RequestAssignEventAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.AssignEventRequested,
    userId,
  };
}

export function createEventRequestedAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): RequestEventAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.EventRequested,
  };
}

export function createGenerateParticipantsZipAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): GenerateParticipantsZipAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.GenerateParticipantsZipRequested,
  };
}

export function createGenerateParticipantsVgZipAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): GenerateParticipantsVgZipAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.GenerateParticipantsVgZipRequested,
  };
}

export function createDeleteParticipantsZipAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): DeleteParticipantsZipAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.DeleteParticipantsZipRequested,
  };
}

export function createDeleteParticipantsVgZipAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): DeleteParticipantsVgZipAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.DeleteParticipantsVgZipRequested,
  };
}

export function createDeleteParticipantsVgVideoAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): DeleteParticipantsVgVideoAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.DeleteParticipantsVgVideoRequested,
  };
}

export function createDeleteParticipantsDataAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): DeleteParticipantsDataAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.DeleteParticipantsDataRequested,
  };
}

function* createEvent(action: CreateEventRequestedAction) {
  formatClipConfiguration(action);
  const request = new CreateEventRequest(action.formData, action.requestingComponent, action.dispatch);

  // @ts-ignore
  yield runApiRequest(request, createEventCreatedAction);
}

function* sendToNlp(action: SendEventToNlpRequestedAction) {
  formatClipConfiguration(action);
  const request = new SendToNlpRequest(action.eventId, action.requestingComponent, action.dispatch);

  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedSingleEntity<EventSendToNlpNormalizers.NormalizedEventsSendToNlpView>) => {
      action.dispatch(createEventSendToNlpAction(result, action.requestingComponent, action.dispatch, action.eventId));

      return action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));
    },
  );
}

function* editEvent(action: EditEventRequestedAction) {
  formatClipConfiguration(action);
  const request = new EditEventRequest(action.eventId, action.formData, action.requestingComponent, action.dispatch);
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );

      return createEventEditedAction(result);
    },
  );
}

function* loadActiveEvents(action: RequestActiveEventsAction) {
  const request = new GetAllEventsRequest(false, action.requestingComponent, action.dispatch);
  // @ts-ignore
  yield runApiRequest(request, createActiveEventsLoadedAction);
}

function* loadArchivedEvents(action: RequestActiveEventsAction) {
  const request = new GetAllEventsRequest(true, action.requestingComponent, action.dispatch);
  // @ts-ignore
  yield runApiRequest(request, createArchivedEventsLoadedAction);
}

function* assignEvent(action: RequestAssignEventAction) {
  const request = new AssignEventRequest(action.eventId, action.userId, action.requestingComponent, action.dispatch);
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );

      return createEventAssignedAction(result);
    },
  );
}

function* loadEvent(action: RequestEventAction) {
  const request = new GetEventRequest(action.eventId, action.requestingComponent, action.dispatch);
  // @ts-ignore
  yield runApiRequest(request, (result: EntityNormalizers.NormalizedSingleEntity<EventNormalizers.NormalizedEvents>) =>
    createEventLoadedAction(action.eventId, result),
  );
}

function* generateParticipantsZip(action: GenerateParticipantsZipAction) {
  const request = new GenerateParticipantsZipRequest(action.eventId, action.requestingComponent, action.dispatch);
  yield runApiRequest(request, () => {
    action.dispatch(createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

    return action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));
  });
}

function* deleteParticipantsZip(action: DeleteParticipantsZipAction) {
  const request = new DeleteParticipantsZipRequest(action.eventId, action.requestingComponent, action.dispatch);
  yield runApiRequest(request, () => {
    action.dispatch(createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

    return action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));
  });
}

function* generateParticipantsVgZip(action: GenerateParticipantsVgZipAction) {
  const request = new GenerateParticipantsVgZipRequest(action.eventId, action.requestingComponent, action.dispatch);
  yield runApiRequest(request, () => {
    action.dispatch(createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

    return action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));
  });
}

function* deleteParticipantsVgZip(action: DeleteParticipantsVgZipAction) {
  const request = new DeleteParticipantsVgZipRequest(action.eventId, action.requestingComponent, action.dispatch);
  yield runApiRequest(request, () => {
    action.dispatch(createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

    return action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));
  });
}

function* deleteParticipantsVgVideo(action: DeleteParticipantsVgVideoAction) {
  const request = new DeleteParticipantsVgVideoRequest(action.eventId, action.requestingComponent, action.dispatch);
  yield runApiRequest(request, () => {
    action.dispatch(createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

    return action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));
  });
}

function* generateVgVideoCeremony(action: GenerateVgVideoCeremonyAction) {
  const request = new CreateVgGeneratorJobRequest(
    action.eventId,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );

  yield runApiRequest(request, () => {
    action.dispatch(createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

    return action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));
  });
}

function* deleteParticipantsData(action: DeleteParticipantsDataAction) {
  const request = new DeleteParticipantsDataRequest(action.eventId, action.requestingComponent, action.dispatch);
  yield runApiRequest(request, () => {
    action.dispatch(createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch));
    action.dispatch(
      createAllEventParticipantsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
    );

    return action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));
  });
}

export function* createEventSaga() {
  yield takeEvery(DataRequestActionTypes.CreateEventRequested, createEvent);
}

export function* editEventSaga() {
  yield takeEvery(DataRequestActionTypes.EditEventRequested, editEvent);
}

export function* loadActiveEventsSaga() {
  yield takeLeading(DataRequestActionTypes.ActiveEventsRequested, loadActiveEvents);
}

export function* loadArchivedEventsSaga() {
  yield takeLeading(DataRequestActionTypes.ArchivedEventsRequested, loadArchivedEvents);
}

export function* assignEventSaga() {
  yield takeEvery(DataRequestActionTypes.AssignEventRequested, assignEvent);
}

export function* loadEventSaga() {
  yield throttleRequests(DataRequestActionTypes.EventRequested, loadEvent, action => action.eventId);
}

export function* generateParticipantsZipSaga() {
  yield takeEvery(DataRequestActionTypes.GenerateParticipantsZipRequested, generateParticipantsZip);
}

export function* deleteParticipantsZipSaga() {
  yield takeEvery(DataRequestActionTypes.DeleteParticipantsZipRequested, deleteParticipantsZip);
}

export function* generateParticipantsVgZipSaga() {
  yield takeEvery(DataRequestActionTypes.GenerateParticipantsVgZipRequested, generateParticipantsVgZip);
}

export function* deleteParticipantsVgZipSaga() {
  yield takeEvery(DataRequestActionTypes.DeleteParticipantsVgZipRequested, deleteParticipantsVgZip);
}

export function* deleteParticipantsVgVideoSaga() {
  yield takeEvery(DataRequestActionTypes.DeleteParticipantsVgVideoRequested, deleteParticipantsVgVideo);
}

export function* generateVgVideoCeremonySaga() {
  yield takeEvery(DataRequestActionTypes.GenerateVgVideoCeremonyRequested, generateVgVideoCeremony);
}

export function* deleteParticipantsDataSaga() {
  yield takeEvery(DataRequestActionTypes.DeleteParticipantsDataRequested, deleteParticipantsData);
}

export function* sendToNlpSaga() {
  yield takeEvery(DataRequestActionTypes.SendToNLP, sendToNlp);
}
